export const SUPPORTED_LOCALES = ['fa', 'en', 'hy', 'ru'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

/** Primary tabs shown directly in admin (FA + EN + HY). */
export const PRIMARY_ADMIN_LOCALES = ['fa', 'en', 'hy'] as const
export type PrimaryAdminLocale = (typeof PRIMARY_ADMIN_LOCALES)[number]

export const LOCALE_DISPLAY_NAMES: Record<Locale, string> = {
  fa: 'فارسی',
  en: 'English',
  hy: 'Հայերեն',
  ru: 'Русский',
}

export function isPrimaryAdminLocale(locale: Locale): locale is PrimaryAdminLocale {
  return (PRIMARY_ADMIN_LOCALES as readonly string[]).includes(locale)
}

/** Locales under the "Other languages" tab (everything except primary tabs). */
export function getOtherAdminLocales(): Locale[] {
  return SUPPORTED_LOCALES.filter((locale) => !isPrimaryAdminLocale(locale))
}

export function getLocaleDisplayName(locale: Locale): string {
  return LOCALE_DISPLAY_NAMES[locale] ?? locale.toUpperCase()
}

export function getLocaleTextDirection(locale: Locale): 'rtl' | 'ltr' {
  return locale === 'fa' ? 'rtl' : 'ltr'
}

const FALLBACK_LOCALE: Locale = 'fa'

/** Normalize "en-US", "fa-IR", etc. to a supported locale code. */
export function normalizeLocale(input?: string | null): Locale | null {
  if (!input || typeof input !== 'string') return null
  const base = input.trim().toLowerCase().split('-')[0]
  if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) {
    return base as Locale
  }
  return null
}

export function detectLocaleFromUrlPath(pathname?: string | null): Locale | null {
  if (!pathname) return null
  const match = pathname.match(/^\/(en|fa|hy|ru)(\/|$)/i)
  return match ? normalizeLocale(match[1]) : null
}

export type LocaleCascadeInput = {
  explicit?: string | null
  htmlLang?: string | null
  urlPath?: string | null
  navigatorLang?: string | null
  acceptLanguage?: string | null
  domainDefault?: string | null
  fallback?: Locale
}

/** Roadmap cascade: explicit → html lang → URL prefix → navigator → domain default → fallback */
export function resolveLocale(input: LocaleCascadeInput = {}): Locale {
  const fallback = input.fallback ?? FALLBACK_LOCALE

  const fromExplicit = normalizeLocale(input.explicit)
  if (fromExplicit) return fromExplicit

  const fromHtml = normalizeLocale(input.htmlLang)
  if (fromHtml) return fromHtml

  const fromUrl = detectLocaleFromUrlPath(input.urlPath)
  if (fromUrl) return fromUrl

  const fromNavigator = normalizeLocale(input.navigatorLang)
  if (fromNavigator) return fromNavigator

  if (input.acceptLanguage) {
    const fromHeader = parseAcceptLanguage(input.acceptLanguage)
    if (fromHeader) return fromHeader
  }

  const fromDomain = normalizeLocale(input.domainDefault)
  if (fromDomain) return fromDomain

  return fallback
}

/** Parse Accept-Language header (first supported match). */
export function parseAcceptLanguage(header?: string | null): Locale | null {
  if (!header) return null
  const parts = header.split(',').map((p) => p.trim().split(';')[0])
  for (const part of parts) {
    const locale = normalizeLocale(part)
    if (locale) return locale
  }
  return null
}

const PERSIAN_SCRIPT_RE = /[\u0600-\u06FF]/
const ARMENIAN_SCRIPT_RE = /[\u0530-\u058F]/

/** Detect default locale from user text (e.g. onboarding messages). */
export function detectDefaultLocaleFromText(text?: string | null): Locale {
  if (!text) return FALLBACK_LOCALE
  if (PERSIAN_SCRIPT_RE.test(text)) return 'fa'
  if (ARMENIAN_SCRIPT_RE.test(text)) return 'hy'
  return 'en'
}

/** Detect default locale for new domains from request + optional conversation text. */
export function detectDefaultLocaleForDomain(options: {
  acceptLanguage?: string | null
  sampleText?: string | null
}): Locale {
  const fromText = options.sampleText
    ? detectDefaultLocaleFromText(options.sampleText)
    : null
  if (fromText && fromText !== FALLBACK_LOCALE) return fromText

  const fromHeader = parseAcceptLanguage(options.acceptLanguage)
  if (fromHeader) return fromHeader

  if (options.sampleText) return detectDefaultLocaleFromText(options.sampleText)

  return FALLBACK_LOCALE
}

export const WIDGET_DEFAULTS: Record<
  Locale,
  { name: string; popupText: string }
> = {
  fa: {
    name: 'پشتیبان هوشمند',
    popupText: 'اگه سوالی داری من اینجام👋',
  },
  en: {
    name: 'Smart Assistant',
    popupText: "Have a question? I'm here to help 👋",
  },
  hy: {
    name: 'Խելացի օգնական',
    popupText: 'Հարց ունե՞ք։ Ես այստեղ եմ օգնելու համար 👋',
  },
  ru: {
    name: 'Умный помощник',
    popupText: 'Есть вопрос? Я здесь, чтобы помочь 👋',
  },
}

export function getWidgetDefaults(locale: Locale) {
  return WIDGET_DEFAULTS[locale] ?? WIDGET_DEFAULTS[FALLBACK_LOCALE]
}

export function getBaseSystemPrompt(locale: Locale): string {
  switch (locale) {
    case 'en':
      return 'You are Atirame, a helpful AI assistant. Be friendly, knowledgeable, and conversational. Reply in English unless the visitor writes in another language.'
    case 'hy':
      return 'Դուք Atirame-ի օգնական եք՝ օգտակար AI ասիստենտ։ Պատասխանեք բարեկամաբար և հստակ։ Պատասխանեք հայերեն, եթե այցելուն այլ լեզվով չի գրում։'
    case 'ru':
      return 'Вы — Atirame, полезный ИИ-ассистент. Отвечайте дружелюбно и по делу. Отвечайте на русском, если посетитель не пишет на другом языке.'
    case 'fa':
    default:
      return 'شما آتیرام هستید، یک دستیار هوشمند مفید. شما دوستانه، دانشمند و پاسخ‌های مفیدی ارائه می‌دهید. پاسخ‌های خود را گفتگویی و جذاب نگه دارید. همیشه به فارسی پاسخ دهید مگر اینکه بازدیدکننده به زبان دیگری بنویسد.'
  }
}
